import React, { useState, useEffect, useRef } from 'react';
import Layout from '../../components/Layout';
import { useSocket } from '../../context/SocketContext';
import { useModal } from '../../context/ModalContext';
import api from '../../utils/api';
import { FiCheck, FiClock, FiRefreshCw, FiAlertCircle, FiPlay, FiPause, FiMaximize2, FiMinimize2, FiPrinter, FiX } from 'react-icons/fi';

function KDS() {
  const socket = useSocket();
  const { alert } = useModal();
  const [kitchenOrders, setKitchenOrders] = useState([]);
  const [kitchenMode, setKitchenMode] = useState('kds');
  const [updatingItemIds, setUpdatingItemIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [previousOrderCount, setPreviousOrderCount] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [printingOrderKeys, setPrintingOrderKeys] = useState(new Set());
  const kdsRef = useRef(null);


  // Fullscreen handlers - Use state-based fullscreen (no browser API, just CSS)
  const enterFullscreen = () => {
    setIsFullscreen(true);
    // Prevent body scroll when in fullscreen
    document.body.style.overflow = 'hidden';
  };

  const exitFullscreen = () => {
    setIsFullscreen(false);
    // Restore body scroll
    document.body.style.overflow = '';
  };

  // Handle ESC key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.key === 'Escape' || e.key === 'Esc') && isFullscreen) {
        exitFullscreen();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullscreen]);

  // Cleanup body overflow on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const fetchKitchenMode = async () => {
    try {
      const settingsResponse = await api.get('/api/settings/restaurant');
      const mode = String(settingsResponse?.data?.kitchen_mode || 'kds').toLowerCase() === 'kot' ? 'kot' : 'kds';
      setKitchenMode(mode);
    } catch {
      // keep previous mode
    }
  };

  useEffect(() => {
    fetchKitchenMode();
    fetchKitchenOrders();
    
    if (socket) {
      socket.on('connect', () => {
        socket.emit('join-kds');
      });

      socket.on('new-order', () => {
        fetchKitchenOrders();
        // Play sound notification for new orders
        playNotificationSound();
      });

      socket.on('order-updated', () => {
        fetchKitchenOrders();
      });
    }

    // Auto-refresh every 7 seconds for real-time updates when tab is active
    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      fetchKitchenOrders();
    }, 7000);
    const modeInterval = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      fetchKitchenMode();
    }, 30000);

    const handleVisibilityChange = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        fetchKitchenOrders();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      clearInterval(modeInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (socket) {
        socket.off('new-order');
        socket.off('order-updated');
      }
    };
  }, [socket]);

  // Check for new orders and play sound
  useEffect(() => {
    const pendingCount = kitchenOrders.filter(item => item.status === 'pending').length;
    if (pendingCount > previousOrderCount && previousOrderCount > 0) {
      playNotificationSound();
    }
    setPreviousOrderCount(pendingCount);
  }, [kitchenOrders]);

  const playNotificationSound = () => {
    // Create a simple beep sound
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 800;
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  };

  const fetchKitchenOrders = async () => {
    try {
      const response = await api.get('/api/kds/orders');
      setKitchenOrders(response.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch kitchen orders:', error);
      setLoading(false);
    }
  };

  const updateItemStatus = async (orderItemId, status) => {
    if (updatingItemIds.has(orderItemId)) {
      return;
    }

    setUpdatingItemIds((prev) => {
      const next = new Set(prev);
      next.add(orderItemId);
      return next;
    });

    // Optimistic UI update for instant feedback
    setKitchenOrders((prev) => prev.map((item) => (
      item.id === orderItemId
        ? {
            ...item,
            status,
            ready_time: status === 'ready' ? (item.ready_time || new Date().toISOString()) : item.ready_time
          }
        : item
    )));

    try {
      await api.patch(`/api/kds/items/${orderItemId}/status`, { status });
      // Background refresh after successful update
      fetchKitchenOrders();
      
      if (socket) {
        socket.emit('order-item-updated', { orderItemId, status });
      }
    } catch (error) {
      console.error('Failed to update item status:', error);
      // Re-sync on failure
      fetchKitchenOrders();
      await alert('Failed to update item status', 'Error', 'error');
    } finally {
      setUpdatingItemIds((prev) => {
        const next = new Set(prev);
        next.delete(orderItemId);
        return next;
      });
    }
  };

  const dismissOrder = async (order) => {
    const itemIds = new Set((order.items || []).map((item) => item.id));
    // Optimistic UI update: remove card instantly
    setKitchenOrders((prev) => prev.filter((item) => !itemIds.has(item.id)));

    try {
      if (order.orderId) {
        await api.post(`/api/kds/orders/${order.orderId}/dismiss`);
      } else {
        await Promise.all(
          (order.items || []).map((item) =>
            api.patch(`/api/kds/items/${item.id}/status`, { status: 'served' })
          )
        );
      }
      fetchKitchenOrders();
    } catch (error) {
      console.error('Failed to dismiss ticket:', error);
      fetchKitchenOrders();
    }
  };

  const getLocalPrintServiceUrls = () => {
    const urls = [];
    const seen = new Set();
    const add = (value) => {
      if (!value) return;
      const normalized = String(value).replace(/\/$/, '');
      if (seen.has(normalized)) return;
      seen.add(normalized);
      urls.push(normalized);
    };

    const origin = (typeof window !== 'undefined' && window.location && window.location.origin)
      ? String(window.location.origin).replace(/\/$/, '')
      : '';
    const port = (typeof window !== 'undefined' && window.location)
      ? String(window.location.port || '')
      : '';

    add(process.env.REACT_APP_PRINT_AGENT_URL);
    if (port === '9101') {
      add(origin);
    }

    add('http://localhost:9101');
    add('http://127.0.0.1:9101');
    return urls;
  };

  const postToLocalPrintService = async (printJob) => {
    const urls = getLocalPrintServiceUrls();
    let lastError = null;

    for (const baseUrl of urls) {
      try {
        const response = await fetch(`${baseUrl}/print`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(printJob)
        });

        if (!response.ok) {
          let message = `HTTP ${response.status}`;
          try {
            const data = await response.clone().json();
            message = data?.error || data?.message || message;
          } catch {
            // ignore parse error
          }
          lastError = new Error(message);
          continue;
        }

        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/octet-stream')) {
          lastError = new Error('Printer not found on local print service');
          continue;
        }

        const payload = await response.json().catch(() => ({}));
        if (!payload?.success) {
          lastError = new Error(payload?.error || payload?.message || 'Print service did not confirm success');
          continue;
        }

        return true;
      } catch (error) {
        lastError = error;
      }
    }

    throw lastError || new Error('No local print service is reachable');
  };

  const getOrderKey = (order) => `${order?.orderId || 'na'}::${order?.orderNumber || 'na'}`;

  const buildKotPrintItems = (orderItems = []) => {
    const grouped = new Map();
    for (const item of orderItems) {
      const name = item?.item_name || 'Item';
      const note = item?.special_instructions || item?.specialInstructions || item?.special_instruction || '';
      const qty = Number(item?.quantity || 1);
      const key = `${name}||${note}`;
      if (grouped.has(key)) {
        grouped.get(key).qty += qty;
      } else {
        grouped.set(key, { qty, name, special_instructions: note });
      }
    }
    return Array.from(grouped.values());
  };

  const printKotForOrder = async (order) => {
    const orderKey = getOrderKey(order);
    if (printingOrderKeys.has(orderKey)) {
      return;
    }

    setPrintingOrderKeys((prev) => {
      const next = new Set(prev);
      next.add(orderKey);
      return next;
    });

    try {
      const items = Array.isArray(order?.items) ? order.items : [];
      if (items.length === 0) {
        await alert('No order items found for this KOT', 'Nothing to Print', 'warning');
        return;
      }

      const first = items[0];
      const printJob = {
        template: 'kot',
        restaurant: 'Restaurant',
        paperWidthMm: 58,
        printer: {
          type: 'windows',
          connection: 'auto'
        },
        kot_order_number: first?.orders?.order_number || order?.orderNumber || order?.orderId,
        table: order?.tableNumber || first?.table_number || 'N/A',
        date: first?.display_time || first?.orders?.created_at || new Date().toISOString(),
        items: buildKotPrintItems(items),
        timestamp: Date.now()
      };

      // enrich with latest printer settings when available
      try {
        const settingsRes = await api.get('/api/settings/restaurant');
        const settings = settingsRes?.data || {};
        if (settings?.name) printJob.restaurant = settings.name;
        if ([58, 76, 80].includes(Number(settings?.printer_paper_width_mm))) {
          printJob.paperWidthMm = Number(settings.printer_paper_width_mm);
        }
        printJob.printer.connection = settings?.printer_preferred_name || 'auto';
      } catch {
        // fallback values above are used if settings fetch fails
      }

      try {
        await postToLocalPrintService(printJob);
      } catch (primaryPrintError) {
        // Retry once with auto queue to handle stale preferred printer names.
        if (printJob?.printer?.connection && printJob.printer.connection !== 'auto') {
          printJob.printer.connection = 'auto';
          await postToLocalPrintService(printJob);
        } else {
          throw primaryPrintError;
        }
      }

      // Mark all items in this order as printed/preparing only after confirmed print
      await Promise.all(items.map(async (item) => {
        if (!item?.id) return;
        await api.patch(`/api/kds/items/${item.id}/status`, { status: 'preparing' });
      }));

      await alert('KOT printed successfully', 'Success', 'success');
      fetchKitchenOrders();
    } catch (error) {
      console.error('Failed to print KOT:', error);
      await alert(
        `Failed to print KOT: ${error?.message || 'Unknown error'}\n\nPlease ensure ElyDine print-agent is running (localhost:9101).`,
        'Print Failed',
        'error'
      );
    } finally {
      setPrintingOrderKeys((prev) => {
        const next = new Set(prev);
        next.delete(orderKey);
        return next;
      });
    }
  };

  const getStatusColor = (status, minutesElapsed) => {
    if (status === 'ready') return 'green';
    if (status === 'preparing') return 'blue';
    if (minutesElapsed > 15) return 'red'; // Delayed
    if (minutesElapsed > 10) return 'orange'; // Warning
    return 'yellow'; // Pending
  };

  const getStatusBorderColor = (status, minutesElapsed) => {
    const color = getStatusColor(status, minutesElapsed);
    const colors = {
      red: 'border-red-500',
      orange: 'border-orange-500',
      yellow: 'border-yellow-400',
      blue: 'border-blue-400',
      green: 'border-green-400'
    };
    return colors[color] || colors.yellow;
  };

  const getStatusBgColor = (status, minutesElapsed) => {
    const color = getStatusColor(status, minutesElapsed);
    const colors = {
      red: 'bg-red-500/20',
      orange: 'bg-orange-500/20',
      yellow: 'bg-yellow-400/20',
      blue: 'bg-blue-400/20',
      green: 'bg-green-400/20'
    };
    return colors[color] || colors.yellow;
  };

  const getTimeElapsed = (timestamp) => {
    if (!timestamp) return 0;
    try {
      // Parse timestamp - ensure it's treated correctly
      let orderDate;
      
      if (typeof timestamp === 'string') {
        // If timestamp is a string, check if it has timezone info
        // Supabase returns timestamps in ISO format, usually with 'Z' or timezone offset
        // If no timezone, assume UTC
        if (!timestamp.includes('Z') && !timestamp.includes('+') && !timestamp.match(/-\d{2}:\d{2}$/)) {
          // No timezone indicator - append 'Z' to force UTC interpretation
          orderDate = new Date(timestamp + 'Z');
        } else {
          // Has timezone info, parse normally
          orderDate = new Date(timestamp);
        }
      } else if (timestamp instanceof Date) {
        // Already a Date object
        orderDate = timestamp;
      } else {
        console.warn('Invalid timestamp format:', timestamp);
        return 0;
      }
      
      // Validate the date
      if (isNaN(orderDate.getTime())) {
        console.error('Invalid timestamp:', timestamp);
        return 0;
      }
      
      // Get current time
      const now = new Date();
      
      // Calculate difference in milliseconds
      // Both are in UTC internally (getTime() returns UTC milliseconds)
      const diffMs = now.getTime() - orderDate.getTime();
      
      // Convert to minutes
      const diffMinutes = Math.floor(diffMs / 60000);
      
      // If difference is negative or suspiciously large (like 330 mins = 5.5 hours = IST offset),
      // it's likely a timezone parsing issue
      if (diffMinutes < 0 || diffMinutes > 1440) {
        // Try alternative: parse as if it's already in IST
        // This handles cases where DB returns IST-formatted strings without timezone
        const orderDateIST = new Date(timestamp);
        const nowIST = new Date();
        const diffMsIST = nowIST.getTime() - orderDateIST.getTime();
        const diffMinutesIST = Math.floor(diffMsIST / 60000);
        
        // If IST calculation gives reasonable result, use it
        if (diffMinutesIST >= 0 && diffMinutesIST <= 1440) {
          return diffMinutesIST;
        }
        
        // If still wrong, return 0 and log
        console.warn('Time calculation issue:', {
          original: diffMinutes,
          ist: diffMinutesIST,
          timestamp,
          orderDateUTC: orderDate.toISOString(),
          nowUTC: now.toISOString()
        });
        return 0;
      }
      
      return diffMinutes;
    } catch (error) {
      console.error('Error calculating time elapsed:', error, 'timestamp:', timestamp);
      return 0;
    }
  };

  const getTimeElapsedSeconds = (timestamp) => {
    if (!timestamp) return 0;
    try {
      // Parse timestamp - ensure UTC interpretation
      let orderDate;
      
      if (typeof timestamp === 'string') {
        // Force UTC interpretation if no timezone info
        if (!timestamp.includes('Z') && !timestamp.includes('+') && !timestamp.match(/-\d{2}:\d{2}$/)) {
          orderDate = new Date(timestamp + 'Z');
        } else {
          orderDate = new Date(timestamp);
        }
      } else if (timestamp instanceof Date) {
        orderDate = timestamp;
      } else {
        return 0;
      }
      
      // Validate the date
      if (isNaN(orderDate.getTime())) {
        return 0;
      }
      
      // Get current time
      const now = new Date();
      
      // Calculate difference in milliseconds
      const diffMs = now.getTime() - orderDate.getTime();
      
      // Convert to seconds
      const diffSeconds = Math.floor(diffMs / 1000);
      
      // Ensure non-negative and reasonable
      if (diffSeconds < 0 || diffSeconds > 86400) { // 24 hours in seconds
        return 0;
      }
      
      return diffSeconds;
    } catch (error) {
      console.error('Error calculating time elapsed:', error);
      return 0;
    }
  };

  // Filter out 'served' status items (completed/billed orders)
  const pendingItems = kitchenOrders
    .filter(item => item.status === 'pending' && item.status !== 'served')
    .sort((a, b) => {
      const timeA = getTimeElapsedSeconds(a.display_time || a.created_at);
      const timeB = getTimeElapsedSeconds(b.display_time || b.created_at);
      return timeA - timeB;
    });

  const preparingItems = kitchenOrders
    .filter(item => item.status === 'preparing' && item.status !== 'served')
    .sort((a, b) => {
      const timeA = getTimeElapsedSeconds(a.display_time || a.created_at);
      const timeB = getTimeElapsedSeconds(b.display_time || b.created_at);
      return timeA - timeB; // Oldest first
    });
  
  const readyItems = kitchenOrders
    .filter(item => item.status === 'ready' && item.status !== 'served')
    .sort((a, b) => {
      const timeA = getTimeElapsedSeconds(a.ready_time || a.display_time || a.created_at);
      const timeB = getTimeElapsedSeconds(b.ready_time || b.display_time || b.created_at);
      return timeA - timeB; // Oldest first
    });

  // Group items by order (complete orders as cards)
  const groupByOrder = (items) => {
    const orderGroups = {};
    items.forEach(item => {
      const orderKey = item.orders?.order_number || `Order-${item.order_id}`;
      const orderId = item.order_id;
      const tableNum = item.table_number || 'N/A';
      const displayTime = item.display_time || item.created_at;
      
      if (!orderGroups[orderKey]) {
        orderGroups[orderKey] = {
          orderNumber: orderKey,
          orderId: orderId,
          tableNumber: tableNum,
          displayTime: displayTime,
          items: []
        };
      }
      orderGroups[orderKey].items.push(item);
    });

    return Object.values(orderGroups).sort((a, b) => {
      // Sort by time (oldest first)
      const timeA = getTimeElapsedSeconds(a.displayTime);
      const timeB = getTimeElapsedSeconds(b.displayTime);
      return timeA - timeB;
    });
  };

  const preparingOrders = groupByOrder(preparingItems);
  const readyOrders = groupByOrder(readyItems);
  const notPrintedOrders = groupByOrder(pendingItems);
  const printedOrders = groupByOrder([...preparingItems, ...readyItems]);
  
  // KDS Content Component
  const KDSContent = () => (
    <div 
      ref={kdsRef}
      className={`${isFullscreen ? 'fixed inset-0 w-screen h-screen overflow-auto' : 'min-h-screen'} bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 ${isFullscreen ? 'p-3' : 'p-3'} z-50`}
    >
      <div className="max-w-[1920px] mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-3">
          <div>
            <h1 className="text-2xl font-bold text-white mb-1 tracking-tight">
              {kitchenMode === 'kot' ? 'Kitchen Order Ticket Monitor' : 'Kitchen Display System'}
            </h1>
            <div className="flex gap-4 text-sm text-gray-300">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                <span>
                  {kitchenMode === 'kot' ? 'Not Printed' : 'Preparing'}:{' '}
                  <span className="text-red-400 font-bold">{kitchenMode === 'kot' ? notPrintedOrders.length : preparingOrders.length}</span>
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>
                  {kitchenMode === 'kot' ? 'Printed' : 'Ready'}:{' '}
                  <span className="text-green-400 font-bold">{kitchenMode === 'kot' ? printedOrders.length : readyOrders.length}</span>
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={fetchKitchenOrders}
              className="bg-gray-800/80 backdrop-blur-sm text-white px-3 py-1.5 rounded-lg hover:bg-gray-700 flex items-center gap-1.5 text-sm font-medium transition-all border border-gray-700"
            >
              <FiRefreshCw className="text-sm" /> Refresh
            </button>
            <button
              onClick={isFullscreen ? exitFullscreen : enterFullscreen}
              className="bg-blue-600/80 backdrop-blur-sm text-white px-3 py-1.5 rounded-lg hover:bg-blue-500 flex items-center gap-1.5 text-sm font-medium transition-all border border-blue-500"
            >
              {isFullscreen ? (
                <>
                  <FiMinimize2 className="text-sm" /> Exit
                </>
              ) : (
                <>
                  <FiMaximize2 className="text-sm" /> Fullscreen
                </>
              )}
            </button>
          </div>
        </div>

          {/* Grid Layout - Orders as Complete Cards */}
          <div className="space-y-3" style={{ maxHeight: isFullscreen ? 'calc(100vh - 100px)' : 'calc(100vh - 120px)', overflowY: 'auto' }}>
            {/* Preparing Orders Section */}
            {(kitchenMode === 'kot' ? notPrintedOrders.length > 0 : preparingOrders.length > 0) && (
              <div>
                <h2 className="text-base font-bold text-white mb-2 flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                  {kitchenMode === 'kot' ? `Not Printed (${notPrintedOrders.length})` : `Preparing (${preparingOrders.length})`}
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-2.5">
                  {(kitchenMode === 'kot' ? notPrintedOrders : preparingOrders).map((order) => {
                    const orderKey = getOrderKey(order);
                    const isPrintingOrder = printingOrderKeys.has(orderKey);
                    const oldestItem = order.items.reduce((oldest, item) => {
                      const itemTime = getTimeElapsedSeconds(item.display_time || item.created_at);
                      const oldestTime = getTimeElapsedSeconds(oldest.display_time || oldest.created_at);
                      return itemTime > oldestTime ? item : oldest;
                    }, order.items[0]);
                    const minutesElapsed = getTimeElapsed(oldestItem?.display_time || order.displayTime);
                    const isDelayed = minutesElapsed > 20;
                    
                    return (
                      <div
                        key={order.orderNumber}
                        className="bg-white rounded-lg shadow-lg overflow-hidden border-2 border-red-500"
                      >
                        {/* Red Header */}
                        <div className="bg-red-500 text-white px-3 py-2">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="text-xs font-semibold">
                                Table: {order.tableNumber}
                              </div>
                              <div className="text-[10px] text-red-100 mt-0.5">
                                {order.orderNumber}
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              {isDelayed && (
                                <span className="bg-red-700 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                                  DELAYED
                                </span>
                              )}
                              <button
                                onClick={() => dismissOrder(order)}
                                title="Dismiss Ticket"
                                aria-label="Dismiss Ticket"
                                className="text-white hover:text-red-200 hover:bg-red-600/60 p-1 rounded transition-colors"
                              >
                                <FiX className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        </div>
                        
                        {/* Order Items */}
                        <div className="p-2 space-y-1 max-h-[250px] overflow-y-auto">
                          {order.items.map((item) => {
                            const itemMinutes = getTimeElapsed(item.display_time || item.created_at);
                            return (
                              <div key={item.id} className="border-b border-gray-100 pb-1 last:border-0">
                                <div className="flex items-start justify-between gap-1">
                                  <div className="flex-1 min-w-0">
                                    <div className="text-xs font-semibold text-gray-900">
                                      {item.quantity}x {item.item_name}
                                    </div>
                                    {item.special_instructions && (
                                      <div className="mt-1 px-1.5 py-0.5 inline-flex items-center rounded bg-yellow-100 border border-yellow-400">
                                        <span className="text-[9px] font-semibold text-yellow-900 uppercase mr-1">
                                          Note
                                        </span>
                                        <span className="text-[10px] text-yellow-900 font-medium">
                                          {item.special_instructions}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center justify-between mt-1">
                                  <span className="text-[10px] text-gray-500">{itemMinutes} min</span>
                                  {kitchenMode !== 'kot' ? (
                                    <button
                                      onClick={() => updateItemStatus(item.id, 'ready')}
                                      disabled={updatingItemIds.has(item.id)}
                                      className="bg-green-500 hover:bg-green-600 text-white text-[10px] font-semibold px-2 py-0.5 rounded transition-colors"
                                    >
                                      {updatingItemIds.has(item.id) ? 'Updating...' : 'Ready'}
                                    </button>
                                  ) : (
                                    <span className="text-[10px] font-semibold text-red-600">Print Pending</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}

                          {kitchenMode === 'kot' && (
                            <div className="pt-2">
                              <button
                                onClick={() => printKotForOrder(order)}
                                disabled={isPrintingOrder}
                                className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white text-xs font-semibold px-2 py-1.5 rounded transition-colors inline-flex items-center justify-center gap-1"
                              >
                                <FiPrinter className="text-xs" />
                                {isPrintingOrder ? 'Printing...' : 'Print KOT'}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Ready Orders Section */}
            {(kitchenMode === 'kot' ? printedOrders.length > 0 : readyOrders.length > 0) && (
              <div>
                <h2 className="text-base font-bold text-white mb-2 flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  {kitchenMode === 'kot' ? `Printed (${printedOrders.length})` : `Ready (${readyOrders.length})`}
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-2.5">
                  {(kitchenMode === 'kot' ? printedOrders : readyOrders).map((order) => {
                    const oldestReadyItem = order.items.find(item => item.ready_time) || order.items[0];
                    const readyMinutes = oldestReadyItem?.ready_time ? getTimeElapsed(oldestReadyItem.ready_time) : 0;
                    const isWaitingLong = readyMinutes > 5;
                    
                    return (
                      <div
                        key={order.orderNumber}
                        className="bg-white rounded-lg shadow-lg overflow-hidden border-2 border-green-500"
                      >
                        {/* Green Header */}
                        <div className="bg-green-500 text-white px-3 py-2">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="text-xs font-semibold">
                                Table: {order.tableNumber}
                              </div>
                              <div className="text-[10px] text-green-100 mt-0.5">
                                {order.orderNumber}
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              {isWaitingLong && (
                                <span className="bg-yellow-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                                  WAITING
                                </span>
                              )}
                              <button
                                onClick={() => dismissOrder(order)}
                                title="Dismiss Ticket"
                                aria-label="Dismiss Ticket"
                                className="text-white hover:text-green-200 hover:bg-green-600/60 p-1 rounded transition-colors"
                              >
                                <FiX className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        </div>
                        
                        {/* Order Items */}
                        <div className="p-2 space-y-1 max-h-[250px] overflow-y-auto">
                          {order.items.map((item) => {
                            const itemReadyMinutes = item.ready_time ? getTimeElapsed(item.ready_time) : 0;
                            return (
                              <div key={item.id} className="border-b border-gray-100 pb-1 last:border-0">
                                <div className="flex items-start justify-between gap-1">
                                  <div className="flex-1 min-w-0">
                                    <div className="text-xs font-semibold text-gray-900">
                                      {item.quantity}x {item.item_name}
                                    </div>
                                    {item.special_instructions && (
                                      <div className="mt-1 px-1.5 py-0.5 inline-flex items-center rounded bg-yellow-100 border border-yellow-400">
                                        <span className="text-[9px] font-semibold text-yellow-900 uppercase mr-1">
                                          Note
                                        </span>
                                        <span className="text-[10px] text-yellow-900 font-medium">
                                          {item.special_instructions}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="text-[10px] text-green-600 font-semibold mt-1">
                                  ✓ Ready {itemReadyMinutes} min ago
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Empty State */}
            {(kitchenMode === 'kot'
              ? (notPrintedOrders.length === 0 && printedOrders.length === 0)
              : (preparingOrders.length === 0 && readyOrders.length === 0)) && (
              <div className="text-gray-400 text-center py-12 text-base font-medium">
                {kitchenMode === 'kot' ? 'No KOT tickets yet' : 'No orders in kitchen'}
              </div>
            )}
          </div>
        </div>
      </div>
  );

  if (loading) {
    const LoadingContent = () => (
      <div className={`${isFullscreen ? 'fixed inset-0 w-screen h-screen' : 'min-h-screen'} bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center z-50`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-400 mx-auto mb-4"></div>
          <div className="text-white text-xl font-semibold">Loading kitchen orders...</div>
        </div>
      </div>
    );

    if (isFullscreen) {
      return <LoadingContent />;
    }
    return (
      <Layout title={kitchenMode === 'kot' ? 'KOT Monitor' : 'KDS'}>
        <LoadingContent />
      </Layout>
    );
  }

  // In fullscreen mode, render only KDS content without Layout
  if (isFullscreen) {
    return <KDSContent />;
  }

  // Normal mode with Layout
  return (
    <Layout title={kitchenMode === 'kot' ? 'KOT Monitor' : 'Kitchen Display System'}>
      <KDSContent />
    </Layout>
  );
}

export default KDS;
